/**
 * black_hole — the PURE rules of a placeable black hole in the sector (shader gaps K, 2026-09-30).
 *
 * The owner: "could you add the black hole lensing effect as we could use that in the future". No destination exists
 * yet: the engine lens (enginex/render/space/blackhole — Bruneton 2020's precomputed beam tracing, a Keplerian disc
 * with Doppler + gravitational shift) is placed on demand by `window.__blackHole({ at?, mass?, disc? })` (SpaceModule,
 * via lib/space/black_hole_look.ts); a future destination calls the same `placeBlackHole` path.
 *
 * Everything decided by NUMBER lives here, three-free, so `verify:black-hole` pins the rule: the mass → r_s scaling,
 * the placement in front of the camera, the disc spec (defaults, clamps, the spin axis from a tilt), the flag under its
 * kill switch, and the stars' mean radiance the lens hands a beam wider than the star filter (the photon ring).
 *
 * NO-CYCLE RULE: numbers in, numbers out. Nothing here imports three, a module or the bus.
 */

export type V3 = { x: number; y: number; z: number };
export type BlackHoleQualityFlag = 'off' | 'low' | 'high';

/** G·M☉ / c² in metres: r_s = 2GM/c² = 2 953.25 m per solar mass (IAU 2015 nominal GM☉). */
export const SCHWARZSCHILD_M_PER_SOLAR_MASS = 2 * 1.3271244e20 / (299_792_458 ** 2);
/**
 * The GAME scale: the sector draws a PHYSICAL hole (r_s = 2 953 m per solar mass) — its sizes then sit on the
 * sector's own ladder (planets 14–240 km, the far plane 3 600 km): the default 10 M☉ hole has r_s ≈ 29.5 km, a
 * 354 km disc, and reads ~27° across from 1 500 km. A destination may pick any mass; this is a lever, not a law.
 */
export const BLACK_HOLE_GAME_SCALE = 1;
export const BLACK_HOLE_DEFAULT_MASS = 10;
/** Mass bounds a lever accepts (M☉): ≥ 0.05 keeps r_s ≥ 150 m (the near plane and the table's u ≤ 0.98 clamp), ≤ 5 000 keeps r_s inside the far plane. */
export const BLACK_HOLE_MASS_MIN = 0.05;
export const BLACK_HOLE_MASS_MAX = 5000;

/** The Schwarzschild radius (m) of a game hole of `massSolar` solar masses. */
export function schwarzschildRadiusM(massSolar: number): number {
  const m = Math.min(BLACK_HOLE_MASS_MAX, Math.max(BLACK_HOLE_MASS_MIN, Number.isFinite(massSolar) ? massSolar : BLACK_HOLE_DEFAULT_MASS));
  return SCHWARZSCHILD_M_PER_SOLAR_MASS * m * BLACK_HOLE_GAME_SCALE;
}

/** The accretion disc a lever or a destination asks for (all optional; `false` = no disc). */
export interface BlackHoleDiscSpec {
  /** Outer radius in r_s (inner is the ISCO, 3 r_s). */
  outer?: number;
  /** The hottest ring's temperature (K) at g = 1. */
  temperature?: number;
  /** The hottest ring's radiance at g = 1, gas at its mean (scene HDR units; the sun disc is 16, the bloom threshold 3.4). */
  luminosity?: number;
  /** 1 = physical Doppler / gravitational shift and g⁴ beaming; 0 = the art-directed uniform disc. */
  beaming?: number;
  /** One orbit of the inner edge, in seconds of game time (the Keplerian shape Ω ∝ r^(−3/2) holds outward). */
  periodS?: number;
  /** The disc's tilt from the ecliptic (y-up) plane, degrees, about the world x axis — or an explicit spin axis. */
  tiltDeg?: number;
  normal?: V3;
  /** The gas texture's contrast: 1 = sheared arcs and knots, 0 = the smooth analytic disc. */
  gas?: number;
}
/**
 * THE DEFAULT LOOK (the realism pass, 2026-10-07 — measured in docs/research/2026-10-07-space-realism/):
 *   temperature 4500 K (was 6500). A real disc is 10⁵–10⁷ K and blue-white to an eye wherever it is seen; every picture
 *     that COLOURS one draws it warm (NASA SVS 13326's hot palette, Bruneton 2020, Marck 1989: hue 0–25°, saturated). At
 *     6500 K the approaching side went blue-white and the display curve left the rest cream; at 4500 K the Doppler shift
 *     runs the disc from near-white (the approaching side, ~6000 K) through gold to deep orange (the receding side and
 *     the rim, ~2700–3100 K) — colour by radius AND by side, which is what the shift physically does to a blackbody.
 *   luminosity 1 (was 3) at g = 1. Under the game's DEFAULT operator (AgX at the space eye's exposure) 3 put the whole
 *     approaching half on the shoulder — flat white, no gradient; at 1 its hot ring (g⁴·T⁴ ≈ 2.5–4, knots twice that)
 *     still crosses the sector's bloom threshold 3.4 while the receding side (≈ 0.15) sits in the dark half of the curve.
 * The lopsidedness itself is physics (g⁴, the `beaming` lever) and is not tuned.
 */
export const DISC_DEFAULTS = { outer: 12, temperature: 4500, luminosity: 1, beaming: 1, periodS: 12, tiltDeg: 12, gas: 1 } as const;
export const DISC_OUTER_MIN = 4, DISC_OUTER_MAX = 60;

export interface ResolvedDisc { on: boolean; outer: number; temperature: number; luminosity: number; beaming: number; spin: number; normal: V3; gas: number }
/** Defaults, clamps, the spin axis: the gas orbits counter-clockwise seen from +normal. */
export function resolveDisc(spec: BlackHoleDiscSpec | boolean | undefined): ResolvedDisc {
  const s: BlackHoleDiscSpec = spec === false ? {} : spec === true || spec === undefined ? {} : spec;
  const num = (v: unknown, d: number): number => (typeof v === 'number' && Number.isFinite(v) ? v : d);
  const outer = Math.min(DISC_OUTER_MAX, Math.max(DISC_OUTER_MIN, num(s.outer, DISC_DEFAULTS.outer)));
  const tilt = (num(s.tiltDeg, DISC_DEFAULTS.tiltDeg) * Math.PI) / 180;
  let normal: V3 = { x: 0, y: Math.cos(tilt), z: Math.sin(tilt) };
  if (s.normal) {
    const l = Math.hypot(s.normal.x, s.normal.y, s.normal.z);
    if (l > 1e-9) normal = { x: s.normal.x / l, y: s.normal.y / l, z: s.normal.z / l };
  }
  const period = Math.max(1, num(s.periodS, DISC_DEFAULTS.periodS));
  return {
    on: spec !== false,
    outer,
    temperature: Math.min(40000, Math.max(1000, num(s.temperature, DISC_DEFAULTS.temperature))),
    luminosity: Math.min(200, Math.max(0, num(s.luminosity, DISC_DEFAULTS.luminosity))),
    beaming: Math.min(1, Math.max(0, num(s.beaming, DISC_DEFAULTS.beaming))),
    spin: (2 * Math.PI) / period,
    normal,
    gas: Math.min(1, Math.max(0, num(s.gas, DISC_DEFAULTS.gas))),
  };
}

/** Where a lever places a hole with no `at`: this far ahead of the camera, in r_s — the disc fills ~a third of the view. */
export const PLACE_AHEAD_RS = 50;
/** The absolute position `distanceRs` r_s ahead of the camera along its unit forward. */
export function placementAhead(camAbs: V3, forward: V3, rsM: number, distanceRs = PLACE_AHEAD_RS): V3 {
  const l = Math.hypot(forward.x, forward.y, forward.z) || 1;
  const d = rsM * distanceRs;
  return { x: camAbs.x + (forward.x / l) * d, y: camAbs.y + (forward.y / l) * d, z: camAbs.z + (forward.z / l) * d };
}
/** THE FP64 LAW: the render position is the absolute one minus the floating origin, in doubles (before any float32). */
export function renderPosition(abs: V3, origin: V3): V3 { return { x: abs.x - origin.x, y: abs.y - origin.y, z: abs.z - origin.z }; }

/** The kill switch (localStorage value 'off' wins over the tier). */
export const BLACK_HOLE_KILL_KEY = 'atlas:black-hole';
/** The flag under its kill switch: the tier's value ('off' | 'low' | 'high'), 'off' when killed. */
export function blackHoleFlag(tierValue: BlackHoleQualityFlag | undefined, killed: boolean): BlackHoleQualityFlag {
  if (killed) return 'off';
  return tierValue === 'low' || tierValue === 'high' ? tierValue : tierValue === 'off' ? 'off' : 'high';
}

/**
 * The mean radiance of the space star field (scene units, averaged over the sphere) — what the lens puts where one
 * pixel's beam spans more sky than the star filter resolves (the photon ring maps the whole sky into a ring a pixel
 * wide: its stars must average, not sparkle — Bruneton's flux-conserving star filter). Each star is the space field's
 * stretched Gaussian at the representative pixel floor: radiance·2πσ² summed, over 4π.
 */
export function starFieldMeanRadiance(sigma: ArrayLike<number>, flux: ArrayLike<number>, p99Peak: number, opts: { shrink: number; pxFloor: number; gamma: number; gammaHi: number; target: number }): number {
  if (!(p99Peak > 0)) return 0;
  let sum = 0;
  const n = Math.min(sigma.length, flux.length);
  for (let i = 0; i < n; i++) {
    const s = Math.max(sigma[i] * opts.shrink, opts.pxFloor);
    const peak = flux[i] / (2 * Math.PI * s * s);
    const x = peak / p99Peak;
    const L = opts.target * Math.pow(x, x < 1 ? opts.gamma : opts.gammaHi);
    sum += L * 2 * Math.PI * s * s;
  }
  return sum / (4 * Math.PI);
}

/** A lever's input, normalised: `at` absolute metres (array or object), `mass` solar masses, `disc` spec or false. */
export interface BlackHolePlaceInput { at?: V3 | [number, number, number]; mass?: number; disc?: BlackHoleDiscSpec | boolean }
export function normalizeAt(at: BlackHolePlaceInput['at']): V3 | null {
  if (!at) return null;
  const v = Array.isArray(at) ? { x: at[0], y: at[1], z: at[2] } : at;
  return Number.isFinite(v.x) && Number.isFinite(v.y) && Number.isFinite(v.z) ? { x: v.x, y: v.y, z: v.z } : null;
}

// ── A star and the hole (space look, 2026-10-08): who draws the sun, and where its image stands ───────────────────
/**
 * HOW THE LENS FILTERS A STAR IT CANNOT RESOLVE (space_sun.starLensSource is the shader; these are its numbers and its
 * CPU mirror). `foot` = one pixel's extent on the star, in disc radii. Under `LENS_POINT_FROM` the star is its own
 * surface with a limb softened by `foot`; past `LENS_POINT_FULL` it is a Gaussian `LENS_POINT_SIGMA·foot` wide.
 */
export const LENS_POINT_SIGMA = 0.6, LENS_POINT_FROM = 0.5, LENS_POINT_FULL = 1.5;
/** The mean of the limb law a + bμ − cμ² over the disc (∫₀¹ I·2r dr with μ = √(1 − r²)): a + 2b/3 − c/2. The Sun's: 0.8067. */
export function limbMean(limb: readonly [number, number, number]): number { return limb[0] + (2 * limb[1]) / 3 - limb[2] / 2; }
/**
 * The unresolved star: a disc of radius 1 seen through a pixel `foot` disc radii wide is drawn as
 * (1 − e^(−1/2σ²))·e^(−r²/(2σ² + ½)), σ = LENS_POINT_SIGMA·foot — whose integral over the plane tends to the disc's
 * own area π as σ grows: the star's FLUX is kept when its image is thinner than a pixel (the mirrored image far
 * off-axis, the images wound round the photon ring), where a point sample of the disc would sparkle.
 */
export function lensPointProfile(r: number, foot: number): number {
  const s2 = Math.max(1e-6, (LENS_POINT_SIGMA * foot) ** 2);
  return (1 - Math.exp(-0.5 / s2)) * Math.exp(-(r * r) / (2 * s2 + 0.5));
}

/** The quad glare stands on a star's image while the image is still a disc: it fades out between these offsets, in Einstein radii (an arc has no point for a lens flare — the bloom carries its glow). */
export const GLARE_ARC_FROM = 0.25, GLARE_ARC_FULL = 0.8;
/** A star whose own surface would hold the hole is not a lens case: the lens draws nothing of it under this many star radii between the two. */
export const STAR_LENS_MIN_RADII = 1.02;

/** How the lens draws one star this frame (`starLensSeat`). */
export interface StarLensSeat {
  /** True = the star stands NEARER than the hole along its own sight line: it is not bent, and is drawn over the hole. */
  front: boolean;
  /** Unit direction to the star's centre — from the HOLE when it is beyond it, from the camera when it is in front. */
  sHat: V3;
  /** r_s ÷ the star's distance from the hole (0 = a sky direction, and 0 in front). */
  kappa: number;
  /** The star's radius ÷ its distance from the hole (beyond) or the camera (in front): the sine of its angular radius from there. */
  rho: number;
  /** The star's angle from the hole's direction as the camera sees it (rad). */
  beta: number;
  /** The weak-field Einstein angle for this star (rad): √(2·u·D_ls/D_s). 0 in front. */
  thetaE: number;
  /** r_s ÷ the hole's distance from the camera. */
  u: number;
  /** The unit direction the camera SEES the star's primary image in — the glare's seat. The star's own direction in front. */
  imageDir: V3;
  /** The image's angle from the hole's direction (rad). */
  imageAngle: number;
  /** The quad glare's gain, 0…1. */
  glare: number;
}

/**
 * THE STAR'S SEAT IN THE LENS. `camToHole` the hole from the camera (metres, any frame — only differences matter),
 * `rsM` its Schwarzschild radius, `starDir` the unit direction from the camera to the star, `starDistM` its distance
 * (Infinity = a sky direction: the Commons Reach's sun), `angR` its angular radius from the camera (rad).
 *
 * BEHIND OR IN FRONT is decided by the star's own sight line: the foot of the perpendicular from the hole onto it is
 * D_l·cos β from the camera — a star nearer than that is passed BEFORE the ray comes round the hole, so the hole does
 * not bend it (and it hides the hole's disc); a star beyond it is seen along the ray's ESCAPE asymptote. A sky
 * direction is always beyond.
 *
 * `imageAngle(u, beta, sourceDistRs)` is the engine's `bhImageAngle` through the lens's own tables (exact); without it
 * the weak field's θ₊ stands in.
 */
export function starLensSeat(
  camToHole: V3, rsM: number, starDir: V3, starDistM: number, angR: number,
  imageAngle?: (u: number, beta: number, sourceDistRs: number) => number | null,
): StarLensSeat {
  const dl = Math.hypot(camToHole.x, camToHole.y, camToHole.z);
  const sl = Math.hypot(starDir.x, starDir.y, starDir.z);
  const ok = dl > 0 && sl > 1e-9 && Number.isFinite(dl) && Number.isFinite(sl) && rsM > 0 && Number.isFinite(rsM);
  const sd: V3 = ok ? { x: starDir.x / sl, y: starDir.y / sl, z: starDir.z / sl } : { x: 0, y: 1, z: 0 };
  const a = Math.min(1.2, Math.max(1e-5, Number.isFinite(angR) ? angR : 0.01));
  const plain: StarLensSeat = { front: true, sHat: sd, kappa: 0, rho: Math.sin(a), beta: Math.PI, thetaE: 0, u: 0, imageDir: sd, imageAngle: Math.PI, glare: 1 };
  if (!ok) return plain;                                       // no hole to speak of: the star as it stands, un-bent
  const h: V3 = { x: camToHole.x / dl, y: camToHole.y / dl, z: camToHole.z / dl };
  const cosB = Math.min(1, Math.max(-1, sd.x * h.x + sd.y * h.y + sd.z * h.z));
  const beta = Math.acos(cosB);
  const u = Math.min(0.98, rsM / dl);
  const finite = Number.isFinite(starDistM) && starDistM > 0;
  if (finite && starDistM < dl * cosB) return { ...plain, beta, u };
  let sHat = sd, kappa = 0, rho = Math.sin(a), dls = 1;
  if (finite) {
    const S: V3 = { x: sd.x * starDistM - camToHole.x, y: sd.y * starDistM - camToHole.y, z: sd.z * starDistM - camToHole.z };
    const d = Math.hypot(S.x, S.y, S.z);
    const R = starDistM * Math.sin(a);
    if (!(d > R * STAR_LENS_MIN_RADII)) return { ...plain, beta, u };   // the hole stands inside the star: nothing to bend
    sHat = { x: S.x / d, y: S.y / d, z: S.z / d };
    kappa = rsM / d; rho = Math.min(0.999, R / d); dls = Math.min(1, d / starDistM);
  }
  const thetaE = Math.sqrt(2 * u * dls);
  const weakPlus = 0.5 * (Math.sqrt(beta * beta + 4 * thetaE * thetaE) + beta);
  let th = weakPlus;
  if (imageAngle) { const e = imageAngle(u, beta, finite ? starDistM / rsM : Infinity); if (e !== null && Number.isFinite(e)) th = e; }
  // the image stands in the plane of camera, hole and star, on the star's side of the hole
  let ex = sd.x - cosB * h.x, ey = sd.y - cosB * h.y, ez = sd.z - cosB * h.z;
  const el = Math.hypot(ex, ey, ez);
  let imageDir = sd, glare = 0;
  if (el > 1e-7) {
    ex /= el; ey /= el; ez /= el;
    const c = Math.cos(th), sn = Math.sin(th);
    imageDir = { x: c * h.x + sn * ex, y: c * h.y + sn * ey, z: c * h.z + sn * ez };
    const y = thetaE > 0 ? beta / thetaE : Infinity;
    const t = Math.min(1, Math.max(0, (y - GLARE_ARC_FROM) / (GLARE_ARC_FULL - GLARE_ARC_FROM)));
    glare = Number.isFinite(y) ? t * t * (3 - 2 * t) : 1;
  }
  return { front: false, sHat, kappa, rho, beta, thetaE, u, imageDir, imageAngle: th, glare };
}

// ── The debug view: one hole placed where it can be SEEN, gone after a minute (debug menu → Admin → Space) ──────────
/** How long a debug-view hole stands (ms). It also leaves with the sector: the exit, a system swap, a sign-out's reload. */
export const DEBUG_VIEW_MS = 60_000;
/** The clear sky the hole keeps between its own edge and anything it must not be drawn across (rad). */
export const DEBUG_VIEW_MARGIN_RAD = (4 * Math.PI) / 180;
/** A star's glare is far wider than its disc: the hole keeps this far from the sun's centre at least (rad). */
export const DEBUG_VIEW_SUN_MIN_RAD = (6 * Math.PI) / 180;
/**
 * Does the debug view still keep off the sun? Since 2026-10-08 the lens BENDS a star that stands beyond the hole (a sky
 * direction always does — the Commons Reach's sun), so the view no longer avoids it: a hole across the sun is the
 * Einstein ring, which is the picture. It still keeps off a star that would stand NEARER than the hole (a derived
 * system's star within the view's 50 r_s): nothing bends there, the star would simply cover the hole.
 */
export function sunBlocksHoleView(starDistM: number, holeDistM: number): boolean {
  return Number.isFinite(starDistM) && starDistM > 0 && starDistM < holeDistM;
}
/** A planet blocks out to its shells (atmosphere 1.025, clouds 1.06 radii); a ringed one out to its ring's outer edge. */
export const DEBUG_VIEW_SHELL_REACH = 1.08;
/**
 * The rings a placement is tried on, nearest the view's centre first: [offset from the view axis (deg), azimuths].
 * The first four stay inside a ~75° view; the rest are the fallback when a planet fills it (the note says where to look).
 */
export const DEBUG_VIEW_RINGS: ReadonlyArray<readonly [number, number]> = [[0, 1], [14, 8], [26, 12], [38, 16], [56, 16], [78, 16], [105, 16], [135, 12], [160, 8], [180, 1]];
/** With no disc the visible body is the shadow: its lensed radius is √27/2 r_s ≈ 2.6 r_s. */
export const SHADOW_RADIUS_RS = Math.sqrt(27) / 2;

/** Something the hole must not be drawn across: a unit direction from the camera and an angular radius (rad). */
export interface ViewBlocker { dir: V3; angR: number; name: string }

/** A sphere (absolute metres) as seen from `camAbs`; null when it is degenerate. A camera inside it blocks the whole sky. */
export function sphereBlocker(camAbs: V3, pos: V3, radius: number, name: string): ViewBlocker | null {
  const dx = pos.x - camAbs.x, dy = pos.y - camAbs.y, dz = pos.z - camAbs.z;
  const d = Math.hypot(dx, dy, dz);
  if (!Number.isFinite(d) || !(radius > 0)) return null; // a NaN position or a body with no size blocks nothing
  if (d <= radius) return { dir: { x: 0, y: 0, z: 1 }, angR: Math.PI, name };
  return { dir: { x: dx / d, y: dy / d, z: dz / d }, angR: Math.asin(radius / d), name };
}

/**
 * A planet as a blocker: out to its ring's outer edge (`ringOuter`, in radii) when it has one, else its shells. A camera
 * INSIDE the ring's reach cannot keep clear of a sphere that holds it — there the planet itself (and its shells) is
 * what must not be covered, and the ring is a thin sheet the view already looks through.
 */
export function bodyBlocker(camAbs: V3, pos: V3, radius: number, ringOuter: number | null | undefined, name: string): ViewBlocker | null {
  const shell = sphereBlocker(camAbs, pos, radius * DEBUG_VIEW_SHELL_REACH, name);
  if (!(ringOuter && ringOuter > DEBUG_VIEW_SHELL_REACH)) return shell;
  const ring = sphereBlocker(camAbs, pos, radius * ringOuter, name);
  return ring && ring.angR < Math.PI ? ring : shell;
}

/** The angular radius given to each sampled point of the viewer's own hull (rad). */
export const DEBUG_VIEW_HULL_POINT_RAD = (1.5 * Math.PI) / 180;
/**
 * The viewer's own hull in a chase view, as blockers: one small disc per sampled point of its box (`fromEye`: the
 * vectors from the eye to those points, any length). A wide flat hull is not a circle — a single disc around it would
 * push the hole out of the picture — so the box is sampled and the hole keeps clear of every sample.
 */
export function hullPointBlockers(fromEye: readonly V3[], name = 'your ship'): ViewBlocker[] {
  const out: ViewBlocker[] = [];
  for (const v of fromEye) {
    const l = Math.hypot(v.x, v.y, v.z);
    if (l > 1e-6 && Number.isFinite(l)) out.push({ dir: { x: v.x / l, y: v.y / l, z: v.z / l }, angR: DEBUG_VIEW_HULL_POINT_RAD, name });
  }
  return out;
}

/** The hole's own angular radius from `distanceRs` r_s out: its disc's outer edge, else the shadow. */
export function holeAngularRadius(distanceRs: number, discOuterRs: number | null): number {
  return Math.atan((discOuterRs && discOuterRs > 0 ? discOuterRs : SHADOW_RADIUS_RS) / Math.max(1e-6, distanceRs));
}

export interface DebugViewPlacement {
  /** Absolute metres. */
  at: V3;
  /** Unit direction from the camera. */
  dir: V3;
  /** Angle off the view axis (deg). */
  offsetDeg: number;
  /** Right (+) / left (−) and up (+) / down (−) of the view axis (deg): where to look. */
  yawDeg: number; pitchDeg: number;
  /** Clear sky between the hole's edge and the nearest blocker (deg; negative = drawn across it). */
  clearanceDeg: number;
  /** True when the margin holds against every blocker. */
  clear: boolean;
  /** The nearest blocker's name ('' with none). */
  nearest: string;
  distanceM: number;
}

/**
 * Where a debug-view hole goes: `distanceRs` r_s from the camera, on the direction NEAREST the view axis whose sky is
 * clear of every blocker by the margin (planets — the lens does not order itself among far bodies — and a star that
 * would stand in FRONT of it: `sunBlocksHoleView`; a star beyond it is bent by the lens and is no blocker). With no
 * clear direction anywhere it takes the clearest one and says so.
 */
export function debugViewPlacement(
  camAbs: V3, forward: V3, up: V3, blockers: readonly ViewBlocker[], rsM: number,
  opts: { distanceRs?: number; discOuterRs?: number | null; marginRad?: number } = {},
): DebugViewPlacement {
  const distanceRs = opts.distanceRs ?? PLACE_AHEAD_RS;
  const margin = opts.marginRad ?? DEBUG_VIEW_MARGIN_RAD;
  const own = holeAngularRadius(distanceRs, opts.discOuterRs === undefined ? DISC_DEFAULTS.outer : opts.discOuterRs);
  // an orthonormal view frame: f ahead, r right (three's camera: forward −Z, up +Y → right +X = f × up), u up
  const fl = Math.hypot(forward.x, forward.y, forward.z);
  const f: V3 = fl > 1e-9 && Number.isFinite(fl) ? { x: forward.x / fl, y: forward.y / fl, z: forward.z / fl } : { x: 0, y: 0, z: -1 };
  const cross = (a: V3, b: V3): V3 => ({ x: a.y * b.z - a.z * b.y, y: a.z * b.x - a.x * b.z, z: a.x * b.y - a.y * b.x });
  const dot = (a: V3, b: V3): number => a.x * b.x + a.y * b.y + a.z * b.z;
  let r = cross(f, up);
  let rl = Math.hypot(r.x, r.y, r.z);
  if (!(rl > 1e-6)) { r = cross(f, Math.abs(f.y) < 0.9 ? { x: 0, y: 1, z: 0 } : { x: 1, y: 0, z: 0 }); rl = Math.hypot(r.x, r.y, r.z); } // looking along `up`
  r = { x: r.x / rl, y: r.y / rl, z: r.z / rl };
  const u = cross(r, f);
  const clearanceOf = (d: V3): { c: number; name: string } => {
    let c = Math.PI, name = '';
    for (const b of blockers) {
      const sep = Math.acos(Math.min(1, Math.max(-1, dot(d, b.dir))));
      const k = sep - b.angR - own;
      if (k < c) { c = k; name = b.name; }
    }
    return { c, name };
  };
  let best: { d: V3; off: number; c: number; name: string } | null = null;
  search: for (const [offDeg, n] of DEBUG_VIEW_RINGS) {
    const th = (offDeg * Math.PI) / 180, st = Math.sin(th), ct = Math.cos(th);
    for (let i = 0; i < n; i++) {
      const ph = (2 * Math.PI * i) / n; // 0 = to the right, then counter-clockwise (up)
      const a = st * Math.cos(ph), b = st * Math.sin(ph);
      const d: V3 = { x: ct * f.x + a * r.x + b * u.x, y: ct * f.y + a * r.y + b * u.y, z: ct * f.z + a * r.z + b * u.z };
      const { c, name } = clearanceOf(d);
      if (!best || c > best.c) best = { d, off: offDeg, c, name };
      if (c >= margin) { best = { d, off: offDeg, c, name }; break search; }
    }
  }
  const pick = best!;
  const dist = rsM * distanceRs;
  const deg = (v: number): number => (v * 180) / Math.PI;
  return {
    at: { x: camAbs.x + pick.d.x * dist, y: camAbs.y + pick.d.y * dist, z: camAbs.z + pick.d.z * dist },
    dir: pick.d,
    offsetDeg: pick.off,
    yawDeg: deg(Math.atan2(dot(pick.d, r), dot(pick.d, f))),
    pitchDeg: deg(Math.asin(Math.min(1, Math.max(-1, dot(pick.d, u))))),
    clearanceDeg: deg(pick.c),
    clear: pick.c >= margin,
    nearest: pick.name,
    distanceM: dist,
  };
}

/** Where to look for it, in words: "dead ahead", "26° to the right, 13° up", "behind you, 45° up", "straight above you". */
export function viewBearingText(p: Pick<DebugViewPlacement, 'offsetDeg' | 'yawDeg' | 'pitchDeg'>): string {
  if (p.offsetDeg < 3) return 'dead ahead';
  if (p.offsetDeg > 177) return 'directly behind you';
  const ay = Math.abs(p.yawDeg), ap = Math.abs(p.pitchDeg);
  if (ap > 75) return p.pitchDeg > 0 ? 'straight above you' : 'straight below you'; // the yaw means nothing this near the pole
  const vert = ap < 3 ? '' : `${Math.round(ap)}° ${p.pitchDeg > 0 ? 'up' : 'down'}`;
  const horiz = ay < 3 ? '' : ay > 150 ? 'behind you' : `${Math.round(ay)}° to the ${p.yawDeg > 0 ? 'right' : 'left'}`;
  return [horiz, vert].filter(Boolean).join(', ') || 'dead ahead';
}

// ── THE BACK SEAT (space look, second pass, 2026-10-08) ────────────────────────────────────────────────────────────
// A far body is an impostor at 100 km — NEARER than the lens's opaque cap (≤ 198 km: 4 depth codes before the far
// plane), so a planet that really stands BEYOND the hole was painted OVER the hole's shadow and disc, as the sun's
// quads once were. While such a body is in the cap's part of the sky the three stand in THREE SEATS:
//   the body beyond the hole   BACK_SEAT_CODES 3 before the far plane (~260 km in the sector's planes) — before the sky pass;
//   the cap                    CAP_FORWARD_CODES 8.5 (~96 km) instead of its usual 4 (198 km);
//   a far body NEARER than the hole, in the same part of the sky   FRONT_SEAT_M 60 km (~14 codes) instead of 100 km.
// WHY THREE: float32 clip space rounds a far vertex's depth by a code or two either way (z and w are separate dot
// products), so two things must stand 4+ codes apart to be ordered on every pixel. With the cap left at 4 codes a body
// seated 2 behind it still showed through 13–17 % of its pixels on WebGPU (the bed, 2026-10-08; WebGL2 0.1 %).
// The body is hidden, not bent: a planet's lensed image is not drawn (the record's open item, with its recipe).
/** Depth codes before the far plane at which a body beyond the hole is seated (the sky is at 0). */
export const BACK_SEAT_CODES = 3;
/** The cap's seat (codes before the far plane) while a body sits behind it; its usual seat is the engine's 4. */
export const CAP_FORWARD_CODES = 8.5;
/** Where a far body NEARER than the hole stands (metres) while the cap is forward and the body is in its part of the sky. */
export const FRONT_SEAT_M = 60_000;
/** The seat is taken this far before the body's reach meets the cap, so the hand-over is never on screen (rad). */
export const BACK_SEAT_MARGIN_RAD = (3 * Math.PI) / 180;
/** The A/B switch: `localStorage atlas:bh-back-seat = 'off'` leaves every far body at the usual anchor. */
export const BACK_SEAT_KEY = 'atlas:bh-back-seat';
/**
 * Is a body BEYOND the hole and in the cap's part of the sky? `camToHole` / `camToBody` in metres (one frame — only
 * differences matter), `capRad` the cap's angular radius (the lens's `coreRad`), `reachM` the body's reach (its
 * shells, its ring). BEYOND is the star's rule (starLensSeat): farther along its own sight line than the foot of the
 * perpendicular from the hole — a body nearer than that hides the hole and keeps its place.
 */
export function bodyBehindCap(camToHole: V3, capRad: number, camToBody: V3, reachM: number, marginRad = BACK_SEAT_MARGIN_RAD): boolean {
  return capZone(camToHole, capRad, camToBody, reachM, marginRad) === 2;
}
/** The same part of the sky, but NEARER than the hole: the body that must keep standing in front of a cap moved forward. */
export function bodyBeforeCap(camToHole: V3, capRad: number, camToBody: V3, reachM: number, marginRad = BACK_SEAT_MARGIN_RAD): boolean {
  return capZone(camToHole, capRad, camToBody, reachM, marginRad) === 1;
}
/** 0 = not in the cap's part of the sky (or a degenerate input) · 1 = in it, nearer than the hole · 2 = in it, beyond the hole. */
function capZone(camToHole: V3, capRad: number, camToBody: V3, reachM: number, marginRad: number): 0 | 1 | 2 {
  const dl = Math.hypot(camToHole.x, camToHole.y, camToHole.z), db = Math.hypot(camToBody.x, camToBody.y, camToBody.z);
  if (!(dl > 0) || !(db > 0) || !Number.isFinite(dl) || !Number.isFinite(db) || !(capRad > 0) || !Number.isFinite(capRad)) return 0;
  const cosB = Math.min(1, Math.max(-1, (camToHole.x * camToBody.x + camToHole.y * camToBody.y + camToHole.z * camToBody.z) / (dl * db)));
  const reach = Number.isFinite(reachM) && reachM > 0 ? Math.asin(Math.min(1, reachM / db)) : 0;
  if (!(Math.acos(cosB) < capRad + reach + (Number.isFinite(marginRad) ? Math.max(0, marginRad) : 0))) return 0;
  return db <= dl * cosB ? 1 : 2;                                 // nearer than the foot of the perpendicular: in front
}
/**
 * Where a body's impostor stands (metres from the camera): a body inside the far anchor at its own distance; a far
 * body at the anchor; a far body behind the cap at the back seat (never farther than it really is).
 */
export function farBodyAnchorM(distM: number, farAnchorM: number, backSeatM: number, behindCap: boolean, frontSeatM = farAnchorM, beforeForwardCap = false): number {
  if (!(distM > farAnchorM)) return distM;
  if (behindCap) return Number.isFinite(backSeatM) ? Math.min(distM, Math.max(farAnchorM, backSeatM)) : farAnchorM;
  // a nearer body before a cap that stands forward: closer than the usual anchor, never closer than half of it
  if (beforeForwardCap && Number.isFinite(frontSeatM)) return Math.min(farAnchorM, Math.max(farAnchorM * 0.5, frontSeatM));
  return farAnchorM;
}

/** Whole seconds a debug-view hole has left (0 when none stands or its time is up). */
export function debugViewLeftS(untilMs: number, nowMs: number): number {
  return untilMs > 0 && nowMs < untilMs ? Math.ceil((untilMs - nowMs) / 1000) : 0;
}
